import React from 'react';
import {Row, Col, Form} from 'react-bootstrap';
import * as yup from 'yup';
import {BaseSettingsForm, DatePickerField} from '@/components/forms';
import {Button} from '@/components/ui';
import FormHelpTooltip from '@/components/utils/FormHelpTooltip';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import i18n from 'i18n';
import { isModuleEnabled } from '@/utils/modules';
import {RestrictedFieldInput} from '@/components/utils/DataNotAvailable';

interface CustomerFormValues {
    firstName: string | null;
    lastName: string | null;
    externalId: string;
    optIn: boolean;
    optInDate: string | null;
}

interface CustomerSettingsFormProps {
    customerId?: number;
    onSave?: () => void;
}

interface CustomerData extends CustomerFormValues {
    id?: number;
}

interface UserData {
    currentModules: {
        'addon-coop'?: boolean;
    };
}

const CustomerSettingsFormSchema = yup.object({
    firstName: yup.string()
        .nullable()
        .max(100, i18n.t('jt_max_length.text', {field: 'first name', length: 100})),
    lastName: yup.string()
        .nullable()
        .max(100, i18n.t('jt_max_length.text', {field: 'last name', length: 100})),
    externalId: yup.string()
        .max(100, i18n.t('jt_max_length.text', {field: 'external id', length: 100}))
        .required(i18n.t('jt_required_field_with_specific_field.text', {field: 'external id'})),
    optIn: yup.boolean(),
    optInDate: yup.date().nullable(),
});

const CustomerSettingsForm: React.FC<CustomerSettingsFormProps> = React.memo((
    {
        customerId = 0,
        onSave = () => { },
    },
) => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const customerRestricted = restrictedFieldKeys.includes('customer_pii');

    const coopEnabled = isModuleEnabled(userData, 'addon-coop');

    const initialValues: CustomerFormValues = {
        firstName: '',
        lastName: '',
        externalId: '',
        optIn: true,
        optInDate: null,
    };

    const customErrorHandling = (error: any, formik: any) => {
        if (error.response?.status === 400) {
            formik.setFieldError('externalId', error.response?.data?.message ?? i18n.t('invalid_data.text'));
            return true;
        }
        return false;
    };

    if (loadingUserData) {
        return null;
    }

    if (customerRestricted) {
        return (
            <div>
                <div className="mb-3">
                    <label className="form-label">{i18n.t('jt_customer_first.text')}</label>
                    <RestrictedFieldInput />
                </div>
                <div className="mb-3">
                    <label className="form-label">{i18n.t('jt_customer_last.text')}</label>
                    <RestrictedFieldInput />
                </div>
                <div className="mb-3">
                    <label className="form-label">{i18n.t('jt_customer_external.text')}</label>
                    <RestrictedFieldInput />
                </div>
            </div>
        );
    }

    return (
        <BaseSettingsForm
            entityId={customerId || undefined}
            resourceUrl={window.URLS.customersListResource}
            storeActionPath="customers.addOrEdit"
            validationSchema={CustomerSettingsFormSchema}
            initialValues={initialValues}
            successMessageKey="jt_customer_successfully.text"
            onSave={onSave}
            customErrorHandling={customErrorHandling}
            additionalStoreActions={[
                {
                    path: 'activeStoryObjectDetails.setReload',
                    value: true,
                },
            ]}
        >
            {(formik, loading) => (
                <>
                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="customerFirstName" >
                                <Form.Label>{i18n.t('jt_customer_first.text')}</Form.Label>
                                <Form.Control
                                    name="firstName"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.firstName ?? ''}
                                    isInvalid={formik.touched.firstName && !!formik.errors.firstName}
                                    autoFocus
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.firstName}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="customerLastName" >
                                <Form.Label>{i18n.t('jt_customer_last.text')}</Form.Label>
                                <Form.Control
                                    name="lastName"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.lastName ?? ''}
                                    isInvalid={formik.touched.lastName && !!formik.errors.lastName}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.lastName}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="customerExternalId" >
                                <Form.Label>{i18n.t('jt_customer_external.text')}</Form.Label>
                                <Form.Control
                                    name="externalId"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.externalId}
                                    disabled={!!customerId}
                                    isInvalid={formik.touched.externalId && !!formik.errors.externalId}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.externalId}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row >

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="optIn" >
                                <Form.Label>{i18n.t('jt_analytics_opt.text')}</Form.Label>
                                <Form.Check
                                    name="optIn"
                                    label={i18n.t('enable_disable.text')}
                                    type="switch"
                                    onChange={(bool: React.ChangeEvent<HTMLInputElement>) => {
                                        formik.setFieldValue('optIn', bool.target.checked);
                                        if (!bool.target.checked) {
                                            formik.setFieldValue('optInDate', null);
                                        }
                                    }}
                                    checked={formik.values.optIn}
                                    isInvalid={!!formik.errors.optIn}
                                    feedback={formik.errors.optIn}
                                    disabled={!coopEnabled}
                                    id="customerOptIn"
                                />
                                {!coopEnabled && (
                                    <Form.Text style={{color: 'red'}}>
                                        {i18n.t('jt_the_following.text')}
                                    </Form.Text>
                                )}
                            </Form.Group>
                        </Col>
                    </Row >

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="optInDate" >
                                <Form.Label>
                                    {i18n.t('jt_select_date.text')}
                                    <FormHelpTooltip>
                                        {i18n.t('jt_events_from.text')}
                                    </FormHelpTooltip>
                                </Form.Label>
                                <DatePickerField
                                    name="optInDate"
                                    value={formik.values.optInDate}
                                    onChange={(value) => formik.setFieldValue('optInDate', value)}
                                    isRequired={false}
                                    error={formik.errors.optInDate}
                                    touched={formik.touched.optInDate}
                                    dateFormat="dd/MM/yyyy"
                                    isClearable={true}
                                    disabled={!formik.values.optIn || !coopEnabled}
                                />
                                {!coopEnabled && (
                                    <Form.Text style={{color: 'red'}}>
                                        {i18n.t('jt_the_following.text')}
                                    </Form.Text>
                                )}
                            </Form.Group>
                        </Col>
                    </Row >

                    <Row className="mt-4">
                        <Col xs={12} className="d-flex justify-content-end">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={formik.isSubmitting}
                                loading={formik.isSubmitting}
                                data-cy="customer-settings-save-btn"
                            >
                                {customerId ? i18n.t('jt_update.text') : i18n.t('jt_save.text')}
                            </Button>
                        </Col>
                    </Row>
                </>
            )}
        </BaseSettingsForm >
    );
});


export default CustomerSettingsForm; 